import { useSelector } from 'react-redux'

import { digObject } from '@domain-group/javascript-utils'

import useReduxAction from '@hooks/useReduxAction'

import { isAdmin, isBrandUser, isMasterAdmin } from '@functions/user'

import { useRelations } from '@hooks/useUser'

type UseCurrentUserOptions = {
  performHttpRequests?: boolean,
}

function useCurrentUser(options: UseCurrentUserOptions = {}) {
  const { performHttpRequests } = options || {}

  useReduxAction(
    'currentUser',
    'loadCurrentUser',
    {
      include_user_frontend_permissions: true,
      include_user_teams_where_team_lead_ids: true,
    },
    [performHttpRequests],
    {
      shouldPerformFn: (entityReducer) => {
        const { errors, loaded, loading } = entityReducer
        return performHttpRequests && !loaded && !loading && !errors.length
      },
    },
  )

  const entityReducer = useSelector(reduxState => reduxState.currentUser)
  const { loaded, loading, result: currentUser } = entityReducer

  const currentUserRelations = useRelations(currentUser)
  const { role } = currentUserRelations

  const permissions = digObject(currentUser, 'frontend_permissions.admin', {})
  const teamsWhereTeamLeadIds = digObject(currentUser, 'teams_where_team_lead_ids', [])
  const isTeamLead = !!teamsWhereTeamLeadIds.length

  return {
    currentUser,
    isAdmin: isAdmin(role.key),
    isBrandUser: isBrandUser(role.key),
    isMasterAdmin: isMasterAdmin(role.key),
    isTeamLead,
    loaded,
    loading,
    permissions,
    teamsWhereTeamLeadIds,
  }
}

export type CurrentUserPayload = ReturnType<typeof useCurrentUser>

export default useCurrentUser
